import type { Bill } from '@/types/bill'
import * as LucideIcons from "lucide-react"
import { ImpactCard } from "./ImpactCard"

interface BillImpactSectionsProps {
  bill?: Bill | null
  className?: string
}

// Type definitions for dynamic schema parsing
interface ScoreData {
  title?: string
  score: number | string
  type: 'positive' | 'negative' | 'neutral'
}

interface SectionData {
  sectionName: string
  title: string
  scores: { key: string; data: ScoreData; title: string }[]
  rationale?: string
  rationaleTitle: string
  icon?: React.ComponentType<any>
}

// Map icon names to actual icon components
const getIconComponent = (iconName?: string): React.ComponentType<any> | undefined => {
  if (!iconName) return undefined
  return (LucideIcons as any)[iconName]
}

// Get default icon for a section based on its name
function getDefaultIcon(sectionName: string): string {
  const iconMappings: Record<string, string> = {
    'summary': 'Target',
    'college_park': 'Building2',
    'higher_education': 'School',
    'financial_aid': 'DollarSign',
    'research': 'Search',
    'operations': 'Settings',
    'compliance': 'Shield',
    'infrastructure': 'Building',
    'financial': 'TrendingUp',
    'academic': 'GraduationCap',
    'administrative': 'Users',
    'cost_analysis': 'TrendingUp',
    'agriculture': 'Wheat',
    'environmental': 'TreePine',
    'health': 'Heart',
    'education': 'BookOpen',
    'technology': 'Laptop',
    'transportation': 'Car',
    'housing': 'Home'
  }

  // Check for exact matches first
  if (iconMappings[sectionName]) {
    return iconMappings[sectionName]
  }

  // Check for partial matches
  for (const [key, icon] of Object.entries(iconMappings)) {
    if (sectionName.includes(key) || key.includes(sectionName)) {
      return icon
    }
  }

  // Default icon
  return 'Info'
}

// Format section name into human-readable title
function formatSectionTitle(sectionName: string): string {
  return sectionName
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

// Format score type into human-readable title
function formatScoreTitle(scoreType: string): string {
  if (scoreType === 'overall_impact') return 'Overall Impact'
  if (scoreType === 'direct_impact') return 'Direct Impact'
  if (scoreType === 'indirect_impact') return 'Indirect Impact'
  if (scoreType === 'agriculture_cost') return 'Agriculture Cost'
  
  return scoreType
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

// Check if a property is a score object (has title, score and type fields)
function isScoreProperty(value: any): boolean {
  return value && 
         typeof value === 'object' && 
         (typeof value.score === 'number' || typeof value.score === 'string') && 
         typeof value.type === 'string' &&
         ['positive', 'negative', 'neutral'].includes(value.type) &&
         (value.title === undefined || typeof value.title === 'string')
}

// Parse monitor_analysis data to extract sections dynamically
function parseMonitorAnalysis(monitorAnalysis: any): SectionData[] {
  if (!monitorAnalysis || typeof monitorAnalysis !== 'object') {
    return []
  }

  const sections: SectionData[] = []

  // Process each top-level section, but skip non-section fields
  for (const [sectionName, sectionValue] of Object.entries(monitorAnalysis)) {
    // Skip top-level fields that are not sections
    if (sectionName === 'title' || typeof sectionValue !== 'object' || sectionValue === null) {
      continue
    }

    const sectionData: SectionData = {
      sectionName,
      // Use section's title from data if available, otherwise format the section name
      title: (sectionValue as any).title || formatSectionTitle(sectionName),
      scores: [],
      rationaleTitle: "Rationale",
      icon: getIconComponent(getDefaultIcon(sectionName))
    }

    // Find all score properties and rationale in this section
    const scoreKeys: string[] = []
    for (const [key, value] of Object.entries(sectionValue as Record<string, any>)) {
      if (key === 'title') {
        // Title is already handled above
        continue
      } else if (key === 'rationale' && typeof value === 'string') {
        sectionData.rationale = value
      } else if (isScoreProperty(value)) {
        scoreKeys.push(key)
      }
    }

    // Sort score keys for consistent ordering
    scoreKeys.sort().forEach((key) => {
      const value = (sectionValue as Record<string, any>)[key]
      // Use subsection's title from data if available, otherwise format the key
      const scoreTitle = value.title || formatScoreTitle(key)

      sectionData.scores.push({
        key,
        data: value as ScoreData,
        title: scoreTitle
      })
    })

    // Only add sections that have at least one score
    if (sectionData.scores.length > 0) {
      sections.push(sectionData)
    }
  }

  return sections
}

export function BillImpactSections({ bill, className }: BillImpactSectionsProps) {
  if (!bill?.monitor_analysis) {
    return (
      <div className={`text-center py-8 text-muted-foreground ${className}`}>
        <p>No impact analysis available for this bill.</p>
      </div>
    )
  }

  const { monitor_analysis } = bill
  
  // Parse the monitor_analysis data dynamically
  const sections = parseMonitorAnalysis(monitor_analysis)
  
  // If no sections found, show empty state
  if (sections.length === 0) {
    return (
      <div className={`text-center py-8 text-muted-foreground ${className}`}>
        <p>No impact sections found in the analysis.</p>
      </div>
    )
  }

  const renderImpactSection = (section: SectionData, index: number) => {
    const { scores, rationale, rationaleTitle } = section
    
    // Get first score (always present)
    const score1 = scores[0]
    if (!score1) return null

    // Get second score if present
    const score2 = scores.length > 1 ? scores[1] : undefined

    return (
      <ImpactCard
        key={section.sectionName}
        title={section.title}
        score1={score1.data}
        score1title={score1.title}
        score2={score2?.data}
        score2title={score2?.title}
        details_title={rationale ? rationaleTitle : undefined}
        details={rationale}
        icon={section.icon}
        className={`z-${(index + 1) * 10}`}
      />
    )
  }

  return (
    <div className={`grid gap-4 ${className}`}>
      {sections.map(renderImpactSection)}
    </div>
  )
}